import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';
import {
  Plus,
  PencilSimple,
  Trash,
  UploadSimple,
  X,
  Folder,
  Tag,
  Star,
} from '@phosphor-icons/react';
import { ProductsSubNav } from './products/Products';
import { confirmAction } from '../components/modals/ConfirmModal';

const categorySchema = z.object({
  name: z.string().min(2, 'Category name must be at least 2 characters'),
  description: z.string().optional(),
  image: z.string().url().optional().or(z.literal('')),
  parentId: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  image?: string;
  position: number;
  parentId?: string;
}

export default function Categories() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const { data: categories = [], isLoading } = useQuery<Category[]>({
    queryKey: ['merchant-categories', activeStoreId],
    queryFn: async () => {
      const res = await apiClient.get<any>(API_ENDPOINTS.PRODUCTS.CATEGORIES);
      return Array.isArray(res) ? res : res?.data || [];
    },
    enabled: !!activeStoreId,
  });

  const { data: store } = useQuery<any>({
    queryKey: ['store-settings', activeStoreId],
    queryFn: async () => {
      return apiClient.get<any>(API_ENDPOINTS.MERCHANT.STORE);
    },
    enabled: !!activeStoreId,
  });

  const updateStoreMutation = useMutation({
    mutationFn: async (settings: any) => {
      return apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, { settings });
    },
    onSuccess: (updatedStore: any) => {
      queryClient.setQueryData(['store-settings', activeStoreId], updatedStore);
      queryClient.invalidateQueries({ queryKey: ['store-settings', activeStoreId] });
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Failed to update store settings');
    },
  });

  const handleUpdateFeatured = (key: string, value: string) => {
    const updatedSettings = {
      ...store?.settings,
      [key]: value,
      [key.replace('Id', '')]: value,
      [key.includes('Id') ? key : key.replace('Category', 'CategoryId')]: value,
    };
    updateStoreMutation.mutate(updatedSettings);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      image: '',
    },
  });

  const uploadImage = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'categories');

    const response: any = await apiClient.getClient().post('/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data.data.url;
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);

    try {
      const imageUrl = await uploadImage(file);
      setValue('image', imageUrl);
      setUploadingImage(false);
    } catch (err: any) {
      alert(err.response?.data?.error?.message || 'Failed to upload image');
      setImagePreview(null);
      setUploadingImage(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      return apiClient.post(API_ENDPOINTS.PRODUCTS.CATEGORIES, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-categories', activeStoreId] });
      closeModal();
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to create category');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      return apiClient.patch(`${API_ENDPOINTS.PRODUCTS.CATEGORIES}/${editingCategory?.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-categories', activeStoreId] });
      closeModal();
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to update category');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiClient.delete(`${API_ENDPOINTS.PRODUCTS.CATEGORIES}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-categories', activeStoreId] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error?.message || 'Failed to delete category');
    },
  });

  const onSubmit = (data: CategoryFormData) => {
    if (editingCategory) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const openEditModal = (cat: Category) => {
    setEditingCategory(cat);
    setValue('name', cat.name);
    setValue('description', cat.description || '');
    setValue('image', cat.image || '');
    setValue('parentId', cat.parentId || '');
    setImagePreview(cat.image || null);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    reset();
    setImagePreview(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    reset();
    setImagePreview(null);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirmAction({
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;
    deleteMutation.mutate(id);
  };

  const totalCategories = categories.length;
  const topLevelCategories = categories.filter((c) => !c.parentId).length;

  return (
    <div className="space-y-5">
      <ProductsSubNav />

      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Product Categories
          </h1>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Organize catalog products into collections and featured sections
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors shrink-0"
        >
          <Plus weight="bold" className="w-3.5 h-3.5" />
          Create Category
        </button>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
              Total Categories
            </span>
            <Tag weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
            {totalCategories}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
              Top-Level Categories
            </span>
            <Folder weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
            {topLevelCategories}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
              Subcategories
            </span>
            <Star weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
            {Math.max(0, totalCategories - topLevelCategories)}
          </p>
        </div>
      </div>

      {/* Featured Homepage Categories Configuration */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
            Featured Homepage Collections
          </h2>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Select which categories highlight on your storefront layout
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] font-bold text-[#202223] dark:text-slate-300 mb-1">
              Featured Category 1
            </label>
            <select
              value={store?.settings?.featuredCategoryId1 || store?.settings?.featuredCategory1 || ''}
              onChange={(e) => handleUpdateFeatured('featuredCategoryId1', e.target.value)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white px-2 focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="">-- None Selected --</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-[#202223] dark:text-slate-300 mb-1">
              Featured Category 2
            </label>
            <select
              value={store?.settings?.featuredCategoryId2 || store?.settings?.featuredCategory2 || ''}
              onChange={(e) => handleUpdateFeatured('featuredCategoryId2', e.target.value)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white px-2 focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="">-- None Selected --</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-[#202223] dark:text-slate-300 mb-1">
              Featured Category 3
            </label>
            <select
              value={store?.settings?.featuredCategoryId3 || store?.settings?.featuredCategory3 || ''}
              onChange={(e) => handleUpdateFeatured('featuredCategoryId3', e.target.value)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white px-2 focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="">-- None Selected --</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Categories Table Directory */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-3 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-14 px-6">
            <Folder weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-2" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Categories Created Yet
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-xs mx-auto">
              Create categories like &apos;Apparel&apos; or &apos;Electronics&apos; to group your products cleanly.
            </p>
            <button
              type="button"
              onClick={openCreateModal}
              className="mt-4 h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
            >
              <Plus weight="bold" className="w-3.5 h-3.5" />
              Create First Category
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Category</th>
                  <th className="py-2.5 px-4">Slug</th>
                  <th className="py-2.5 px-4">Description</th>
                  <th className="py-2.5 px-4">Parent</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {categories.map((cat) => {
                  const parentCat = categories.find((c) => c.id === cat.parentId);
                  return (
                    <tr
                      key={cat.id}
                      className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {cat.image ? (
                            <img
                              src={cat.image}
                              alt={cat.name}
                              className="w-9 h-9 object-cover rounded-lg border border-[#E1E4E8] shrink-0"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#006838] font-bold text-xs shrink-0">
                              {cat.name.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <Link
                              to={`/categories/${cat.id}`}
                              className="font-semibold text-[#202223] dark:text-white hover:text-[#006838]"
                            >
                              {cat.name}
                            </Link>
                          </div>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <code className="bg-[#F6F6F7] dark:bg-slate-800 px-2 py-0.5 rounded text-[11px] font-mono text-[#5C5F62] dark:text-slate-300">
                          {cat.slug}
                        </code>
                      </td>

                      <td className="py-3 px-4 text-[#5C5F62] dark:text-slate-400 max-w-xs truncate">
                        {cat.description || '—'}
                      </td>

                      <td className="py-3 px-4">
                        {parentCat ? (
                          <span className="bg-[#EBF5FA] text-[#006FBB] rounded px-2 py-0.5 text-[10px] font-semibold">
                            {parentCat.name}
                          </span>
                        ) : (
                          <span className="text-[#8C9196] text-[11px]">Top-level</span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(cat)}
                            className="p-1 rounded hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
                            title="Edit Category"
                          >
                            <PencilSimple weight="bold" className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(cat.id)}
                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 transition-colors"
                            title="Delete Category"
                          >
                            <Trash weight="bold" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl max-w-md w-full p-5 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
              <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
                {editingCategory ? 'Edit Category' : 'Create Category'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-[#5C5F62] hover:text-[#202223] dark:hover:text-white"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-[#202223] dark:text-white mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  placeholder="e.g. Footwear"
                  className="w-full h-8 px-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
                {errors.name && (
                  <p className="text-red-600 text-[11px] mt-0.5">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block font-semibold text-[#202223] dark:text-white mb-1">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  rows={2}
                  placeholder="Optional summary..."
                  className="w-full p-2 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                />
              </div>

              <div>
                <label className="block font-semibold text-[#202223] dark:text-white mb-1">
                  Parent Category
                </label>
                <select
                  {...register('parentId')}
                  className="w-full h-8 px-2 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                >
                  <option value="">-- Top-level category --</option>
                  {categories
                    .filter((c) => c.id !== editingCategory?.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-[#202223] dark:text-white mb-1">
                  Category Image
                </label>
                <div className="flex items-center gap-3">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-12 h-12 object-cover rounded-lg border border-[#D2D5D9]"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 border border-dashed border-[#D2D5D9] flex items-center justify-center text-[#8C9196]">
                      <UploadSimple weight="bold" className="w-5 h-5" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white hover:bg-[#F6F6F7]"
                  >
                    {uploadingImage ? 'Uploading...' : 'Choose Image'}
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E1E4E8] dark:border-slate-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="h-8 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 text-xs font-semibold text-[#5C5F62] hover:bg-[#F6F6F7]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending || uploadingImage}
                  className="h-8 px-4 rounded-lg bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingCategory
                    ? 'Save Changes'
                    : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
